/**
 * 📝 [Phase 102 — 2026-07-09] Section "Mes contrats" pour Mon Espace.
 *
 * Rôle
 * ────
 * Affiche l'historique complet des contrats signés de l'employé courant,
 * avec pour chaque contrat :
 *   - Titre + année + date de signature
 *   - Boutons **Consulter** (ouvre le PDF dans un nouvel onglet)
 *              **Télécharger** (téléchargement blob du PDF)
 *
 * Endpoints consommés :
 *   - GET /api/contracts/me/list  → liste des contrats signés
 *   - GET /api/contracts/signature/{id}/pdf  → stream du PDF GridFS
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { FileText, Download, Eye, ShieldCheck, Loader2, Mail } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from "@/components/ui/dialog";

const API_URL = (typeof window !== "undefined" ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || "";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("fr-CA", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

function getAuthToken() {
  try {
    return (
      localStorage.getItem("auth_token") ||
      sessionStorage.getItem("auth_token") ||
      ""
    );
  } catch {
    return "";
  }
}

async function fetchPdfBlob(signatureId) {
  const token = getAuthToken();
  const res = await axios.get(
    `${API_URL}/api/contracts/signature/${signatureId}/pdf`,
    {
      responseType: "blob",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }
  );
  return res.data;
}

export function MesContratsSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  // 🚀 [v13.56] Bouton "M'envoyer une copie par courriel"
  const [resendModal, setResendModal] = useState(null);  // { signatureId, filename }
  const [resending, setResending] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const token = getAuthToken();
        const res = await axios.get(`${API_URL}/api/contracts/me/list`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        setItems(res.data?.contrats || []);
      } catch (err) {
        // Si l'utilisateur n'est pas lié à un employé, on n'affiche rien
        setItems([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleConsulter = async (signatureId) => {
    if (!signatureId) return;
    setBusyId(signatureId);
    try {
      const blob = await fetchPdfBlob(signatureId);
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
      // Ne pas révoquer immédiatement : l'onglet a besoin du blob
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      const msg = err?.response?.status === 404
        ? "PDF non disponible pour ce contrat (signé avant Phase 102)."
        : "Erreur lors de la récupération du PDF.";
      toast.error(msg);
    } finally {
      setBusyId(null);
    }
  };

  const handleTelecharger = async (signatureId, filename) => {
    if (!signatureId) return;
    setBusyId(signatureId);
    try {
      const blob = await fetchPdfBlob(signatureId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || `contrat_${signatureId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Téléchargement lancé");
    } catch (err) {
      const msg = err?.response?.status === 404
        ? "PDF non disponible pour ce contrat (signé avant Phase 102)."
        : "Erreur lors du téléchargement.";
      toast.error(msg);
    } finally {
      setBusyId(null);
    }
  };

  // 🚀 [v13.56] Envoyer copie par courriel
  const handleResendEmail = async () => {
    if (!resendModal?.signatureId) return;
    setResending(true);
    try {
      const token = getAuthToken();
      const r = await axios.post(
        `${API_URL}/api/contracts/me/${resendModal.signatureId}/resend`,
        {},
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      toast.success(`Copie envoyée à ${r.data.email}`);
      setResendModal(null);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        toast.error("Trop d'envois — patientez 1 heure avant de réessayer.");
      } else if (status === 404) {
        toast.error("PDF non disponible pour ce contrat.");
      } else {
        toast.error(err?.response?.data?.detail || "Erreur envoi courriel");
      }
    } finally {
      setResending(false);
    }
  };

  return (
    <Card data-testid="mes-contrats-section">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <FileText className="w-4 h-4 text-slate-500" /> Mes contrats
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-slate-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Chargement de l&apos;historique…
          </div>
        ) : items.length === 0 ? (
          <div
            className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm text-slate-600"
            data-testid="mes-contrats-empty"
          >
            <FileText className="w-4 h-4 text-slate-400" />
            Aucun contrat signé pour le moment.
          </div>
        ) : (
          <div className="space-y-2" data-testid="mes-contrats-list">
            {items.map((c) => (
              <div
                key={c.id}
                className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 border border-slate-200 rounded-lg bg-white hover:bg-slate-50 transition-colors"
                data-testid={`mes-contrats-item-${c.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-sm text-slate-800 truncate">
                      {c.template_titre || "Contrat de travail"}
                    </p>
                    {c.annee && (
                      <Badge variant="outline" className="text-xs">
                        {c.annee}
                      </Badge>
                    )}
                    <Badge
                      className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 text-xs"
                      data-testid={`mes-contrats-signe-${c.id}`}
                    >
                      <ShieldCheck className="w-3 h-3 mr-1" /> Signé
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Signé le {formatDate(c.date_signature)}
                    {c.signature_nom ? ` par ${c.signature_nom}` : ""}
                  </p>
                  {c?.signature_meta?.signature_hash && (
                    <p className="text-[10px] text-slate-400 mt-0.5 font-mono truncate">
                      Empreinte : {c.signature_meta.signature_hash.slice(0, 16)}…
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {c.has_pdf ? (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleConsulter(c.id)}
                        disabled={busyId === c.id}
                        data-testid={`mes-contrats-consulter-${c.id}`}
                      >
                        {busyId === c.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Eye className="w-4 h-4 mr-1" /> Consulter
                          </>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        className="bg-blue-600 hover:bg-blue-700"
                        onClick={() => handleTelecharger(c.id, c.pdf_filename)}
                        disabled={busyId === c.id}
                        data-testid={`mes-contrats-telecharger-${c.id}`}
                      >
                        <Download className="w-4 h-4 mr-1" /> Télécharger
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-purple-700 border-purple-300 hover:bg-purple-50"
                        onClick={() => setResendModal({ signatureId: c.id, filename: c.pdf_filename })}
                        disabled={busyId === c.id || resending}
                        data-testid={`mes-contrats-envoyer-copie-${c.id}`}
                      >
                        <Mail className="w-4 h-4 mr-1" /> M&apos;envoyer une copie
                      </Button>
                    </>
                  ) : (
                    <Badge
                      variant="outline"
                      className="text-xs text-slate-500"
                      data-testid={`mes-contrats-no-pdf-${c.id}`}
                    >
                      PDF indisponible (contrat pré-Phase 102)
                    </Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* 🚀 [v13.56] Modal confirmation envoi copie */}
      <Dialog open={!!resendModal} onOpenChange={v => !v && setResendModal(null)}>
        <DialogContent data-testid="mes-contrats-resend-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-purple-600" /> M&apos;envoyer une copie
            </DialogTitle>
            <DialogDescription>
              Une copie du contrat sera envoyée à votre adresse courriel enregistrée.
            </DialogDescription>
          </DialogHeader>
          <div className="text-xs text-slate-500 py-2">
            Limite : 3 envois par heure pour ce contrat.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResendModal(null)} disabled={resending} data-testid="mes-contrats-resend-cancel">
              Annuler
            </Button>
            <Button
              onClick={handleResendEmail}
              disabled={resending}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="mes-contrats-resend-confirm"
            >
              {resending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
              Envoyer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default MesContratsSection;
